import { FlexColumn, FlexRow, StyledButton, useLoading } from '@gorila-shared-ui/components';
import { useEffect, useMemo, useState } from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../constants/app';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import { createUser, updateUser } from '../../services/usersService';
import {
  editConfirmPasswordUserState,
  editUserErrorState,
  editUserState,
  editUserTouchedState,
  UserChangePasswordState,
} from '../../storage/userStates';
import { User } from '../../types/users';
import { UserFormSteps } from './userForm/UserFormSteps';

type Props = {
  userId?: string;
  user?: User;
  onCancel?: () => void;
  onSubmitFinished?: () => void;
};
export function UserForm({ user, userId, onCancel, onSubmitFinished }: Readonly<Props>) {
  const editMode = userId !== undefined && user !== undefined;
  const { css, theme } = useStyles();
  const { loading, startLoading, stopLoading } = useLoading();
  const [form, setForm] = useRecoilState(editUserState);
  const confirmPassword = useRecoilValue(editConfirmPasswordUserState);
  const changePassword = useRecoilValue(UserChangePasswordState);
  const formHasError = useRecoilValue(editUserErrorState);
  const passwordIsValid = !editMode || changePassword ? !!confirmPassword && !!form.password : true;
  const passwordsDidMatch = confirmPassword === form.password;
  const resetEditUserState = useResetRecoilState(editUserState);
  const resetEditUserTouchedState = useResetRecoilState(editUserTouchedState);
  const resetConfirmPassword = useResetRecoilState(editConfirmPasswordUserState);
  const resetChangePassword = useResetRecoilState(UserChangePasswordState);
  const { showFailFeedback, showPositiveFeedback } = useFeedback();

  const totalSteps = 3;
  const [currentStep, setCurrentStep] = useState(0);
  const isLastStep = currentStep + 1 === totalSteps;

  const canContinue = useMemo(() => {
    if (currentStep === 0) return !formHasError && passwordIsValid && passwordsDidMatch;
    else if (currentStep === 1) return form.schedule.length !== 0;
  }, [formHasError, passwordIsValid, passwordsDidMatch, currentStep, form]);

  const canSubmit =
    form.schedule.length !== 0 && form.skills.length !== 0 && !formHasError && passwordIsValid && passwordsDidMatch;

  useEffect(() => {
    return () => {
      clearStates();
    };
  }, []);

  useEffect(() => {
    if (!user) return;
    setForm((prev) => ({
      ...prev,
      ...user,
      tenant: user.tenant?._id,
      company: user.company?._id,
    }));
  }, [user]);

  const clearStates = () => {
    resetEditUserState();
    resetEditUserTouchedState();
    resetConfirmPassword();
    resetChangePassword();
  };

  const onSubmit = async () => {
    const onSuccess = (successMessage: string) => {
      showPositiveFeedback(successMessage);
      clearStates();
      onSubmitFinished && onSubmitFinished();
    };
    startLoading();
    if (editMode) {
      const { error } = await updateUser(userId, form);
      if (!error) {
        onSuccess(FEEDBACK.edited(FEEDBACK_PREFIXES.user, form.firstName));
      } else {
        showFailFeedback(error);
      }
    } else {
      const { error } = await createUser(form);
      if (!error) {
        onSuccess(FEEDBACK.created(FEEDBACK_PREFIXES.user));
      } else {
        showFailFeedback(error);
      }
    }
    stopLoading();
  };

  return (
    <FlexColumn
      classNames={css({
        justifyContent: 'space-between',
        overflow: 'hidden',
        minWidth: '525px',
      })}
    >
      <FlexColumn
        classNames={css({
          overflow: 'auto',
          height: '100%',
        })}
      >
        <UserFormSteps
          user={user}
          currentStep={currentStep}
        />
      </FlexColumn>
      <FlexRow classNames={`${css({ alignItems: 'center', justifyContent: 'flex-end' })}`}>
        <FlexRow gap={theme.sizing.scale300}>
          {currentStep > 0 && (
            <StyledButton
              kind="tertiary"
              onClick={() => setCurrentStep((prev) => prev - 1)}
            >
              Regresar
            </StyledButton>
          )}
          {user?._id && (
            <StyledButton
              kind="tertiary"
              onClick={onCancel}
            >
              Cancelar
            </StyledButton>
          )}
          {!isLastStep && (
            <StyledButton
              onClick={() => setCurrentStep((prev) => prev + 1)}
              disabled={!canContinue}
            >
              Siguiente
            </StyledButton>
          )}
          {isLastStep && (
            <StyledButton
              onClick={onSubmit}
              isLoading={loading}
              disabled={!canSubmit}
            >
              Guardar
            </StyledButton>
          )}
        </FlexRow>
      </FlexRow>
    </FlexColumn>
  );
}
